import ClockIcon from '@clearkit/icons/glyphs/Clock';
import SupportIcon from '@clearkit/icons/glyphs/Support';
import UserIcon from '@clearkit/icons/glyphs/User';
import WarningIcon from '@clearkit/icons/glyphs/Warning';
import { Link } from 'react-router-dom';

import AudienceServiceIcon from '~/components/audiences/ServiceIcon';
import StatusIcon from '~/components/audiences/StatusIcon';
import {
  audienceHealthLabel,
  audienceStatuses,
  audienceStatusMessage,
  audienceSubtypes,
  readableAudienceEstimatedSize,
  readableAudiencePushRate,
  readableAudienceService,
} from '~/lib/audiences';
import paths from '~/lib/paths';
import AudiencesHealthIndicator from '~/pages/audiences/components/HealthIndicator';

import People from './People.svg';
import styles from './styles';

const AudienceSummaryBox = ({
  audience,
  audience: {
    externalAccountId,
    externalAccountName,
    externalEstimatedSize,
    externalEstimatedSizeChange,
    externalName,
    id,
    pushRate,
    segment,
    service,
    status,
    subtype,
  },
  className,
  classes,
}) => {
  const readableEstimatedSize = readableAudienceEstimatedSize(
    externalEstimatedSize,
  );
  const readableServiceName = readableAudienceService(service);

  return (
    <div className={classnames(classes.root, className)}>
      <div
        className={classnames(classes.box, classes.boxSummary, {
          [classes.contactAudienceSummary]:
            subtype === audienceSubtypes.contacts ||
            subtype === audienceSubtypes.managedContacts,
          [classes.prospectAudienceSummary]:
            subtype === audienceSubtypes.companySegmentProspects ||
            subtype === audienceSubtypes.prospects,
          [classes.visitorAudienceSummary]:
            subtype === audienceSubtypes.visitors ||
            subtype === audienceSubtypes.managedVisitors,
        })}>
        <People className={classes.peopleImage} />

        {status === audienceStatuses.initializing ? (
          <div className="flex flex-col justify-center h-full px-3">
            <ClockIcon width="32" height="32" className="mx-auto mb-3" />
            <span>Waiting for your audience to sync</span>
          </div>
        ) : externalEstimatedSize === null ? (
          <div className="flex flex-col justify-center h-full px-3">
            <SupportIcon width="32" height="32" className="mx-auto mb-3" />
            <span>Unknown audience size</span>
            <small className="opacity-70">
              We don't currently support metrics for this audience type
            </small>
          </div>
        ) : (
          <>
            <div className={classes.totalMatches}>
              <strong>{readableEstimatedSize}</strong>
              <div className={classes.matchesSummary}>
                estimated matches on {readableServiceName}
              </div>

              {!!externalEstimatedSizeChange && (
                <div className={classes.sizeChange}>
                  {externalEstimatedSizeChange > 0
                    ? `+${externalEstimatedSizeChange.toLocaleString()} from the previous sync`
                    : `${externalEstimatedSizeChange.toLocaleString()} from the previous sync`}
                </div>
              )}
            </div>

            <div className={classes.footer}>
              {externalEstimatedSize > 0 ? (
                <>
                  <AudiencesHealthIndicator
                    audience={audience}
                    className={classes.healthIndicator}
                    variant="white"
                  />
                  {audienceHealthLabel(externalEstimatedSize)}
                </>
              ) : (
                <div className={classes.warning}>
                  <WarningIcon />

                  <span>
                    If you're expecting matches in this audience, it could be
                    that your source criteria aren't returning results.{' '}
                    {segment ? (
                      <Link to={paths.segmentEdit(segment.type, segment.id)}>
                        Check your source segment criteria
                      </Link>
                    ) : (
                      <Link to={paths.audienceEdit(id)}>
                        Check your audience criteria
                      </Link>
                    )}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={classes.lastSyncBox}>
        <div className={classes.boxHeader}>
          <AudienceServiceIcon
            audience={audience}
            className={classes.serviceIcon}
            size={40}
          />

          <div className={classes.externalInfo}>
            <span className={classes.externalName}>{externalName}</span>

            <div className={classes.externalAccount}>
              <UserIcon width="16" height="16" className="flex-shrink-0 mr-1" />
              <span className="truncate">
                {externalAccountName}
                <span className={classes.middot}>&middot;</span>
                {externalAccountId}
              </span>
            </div>
          </div>
        </div>
        <div className={classes.boxContents}>
          <div className={classes.latestSync}>
            <div
              className={classnames(classes.status, {
                [classes.statusSuccess]: status === audienceStatuses.active,
              })}>
              <StatusIcon audience={audience} size={16} />
              {audienceStatusMessage(audience)}
            </div>

            {externalEstimatedSizeChange > 0 && (
              <div className={classes.lastSync}>
                Synced {externalEstimatedSizeChange.toLocaleString()} new
                matches to {readableServiceName}
              </div>
            )}
          </div>

          {pushRate && (
            <div className={classes.nextSearchAt}>
              <ClockIcon width="16" height="16" />

              <span>Syncs {readableAudiencePushRate(pushRate)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AudienceSummaryBox.propTypes = {
  audience: PropTypes.object.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AudienceSummaryBox);
